/**
 * Bridge Strategy — Generator 提交
 *
 * 将 section JS 收集的 prompt + file + formValues 提交到目标生成器页面。
 * 内部流程：requireLogin（如有文件）→ 上传文件 → writeToTargetStore → navigate
 *
 * generatorKey 对齐 UGCGenerator/config.ts 的 generatorOptions。
 */
import type { LandingBridgeImpl } from '../LandingBridgeImpl';
import type { BridgeGeneratorConfig } from '../types';

// ─── generatorKey 直接作为 writeToTargetStore 的 targetKey（与 generatorOptions 对齐） ──

// ─── generatorKey → 路由 + Tab 配置（镜像 generatorOptions） ────────────────
const GENERATOR_ROUTE_CONFIG: Record<
  string,
  { route: string; textTab?: string; imageTab?: string }
> = {
  'ugc-video-ads': {
    route: '/ai-video-generator',
    textTab: 'text-to-video',
    imageTab: 'image-to-video',
  },
  'video-from-product-image': {
    route: '/ai-product-avatar',
    imageTab: 'product',
  },
  'video-from-avatar-image': {
    route: '/ai-product-avatar',
    imageTab: 'avatar',
  },
  'generate-image': {
    route: '/ai-image-generator',
    textTab: 'text-to-image',
    imageTab: 'image-to-image',
  },
};

export function registerGeneratorStrategy(bridge: LandingBridgeImpl): void {
  bridge.register('submitToGenerator', (config: BridgeGeneratorConfig) => {
    const {
      generatorKey,
      prompt = '',
      file,
      lastFrameFile,
      formValues,
      onError,
    } = config;

    const routeConfig = GENERATOR_ROUTE_CONFIG[generatorKey];
    if (!routeConfig) {
      const msg = `unknown generatorKey: ${generatorKey}`;
      console.warn('[LandingBridge]', msg);
      onError?.(msg);
      return;
    }

    const hasFile = !!file;
    // 有文件 → imageTab；无文件 → textTab，若无 textTab 则 fallback 到 imageTab
    const tab = hasFile
      ? routeConfig.imageTab
      : routeConfig.textTab || routeConfig.imageTab;

    /** 上传单个文件，返回 URL */
    const uploadFile = async (f: File): Promise<string> => {
      const { Uploader } = await import('@/utils/Uploader/CommonUploader/index');
      const uploader = Uploader.getInstance();
      const url = await uploader.upload({
        file: f,
        tokenData: { fileName: f.name, dirSceneType: 20 },
      });
      if (!url) throw new Error('Upload returned empty URL');
      return url;
    };

    /** 写入目标 Store + 导航（对齐 UGCGenerator 组件行为） */
    const doNavigate = async (
      uploadedFileUrl?: string,
      uploadedLastFrameUrl?: string,
    ) => {
      const { writeToTargetStore } =
        await import('@/landing/components/UGCGenerator/utils/writeToTargetStore');
      const { useUGCNavigationStore } =
        await import('@/landing/components/UGCGenerator/store');

      if (uploadedFileUrl && file) {
        const { FILE_STATUS } = await import('@/components/FileUpload');
        const uploadedFile = {
          uid: 'bridge-' + Date.now(),
          name: file.name,
          status: FILE_STATUS.SUCCESS,
          url: uploadedFileUrl,
        };

        // 构造尾帧文件对象（如有）
        let uploadedLastFrame;
        if (uploadedLastFrameUrl && lastFrameFile) {
          uploadedLastFrame = {
            uid: 'bridge-last-' + Date.now(),
            name: lastFrameFile.name,
            status: FILE_STATUS.SUCCESS,
            url: uploadedLastFrameUrl,
          };
        }

        writeToTargetStore(
          generatorKey,
          prompt,
          [uploadedFile as any],
          true,
          formValues,
          uploadedLastFrame ? [uploadedLastFrame as any] : undefined,
        );
      } else {
        writeToTargetStore(generatorKey, prompt, [], false, formValues);
      }

      // 通过 Navigation Store 传递 Tab Key（与 UGCGenerator 一致）
      if (tab) {
        useUGCNavigationStore.getState().setTargetTabKey(tab);
      }

      bridge.navigate(routeConfig.route);
    };

    if (file) {
      // 有文件：先确保登录，再上传，再写 Store + 跳转
      bridge.requireLogin(() => {
        (async () => {
          try {
            const uploadedUrl = await uploadFile(file);

            // 上传尾帧（如有）
            let lastFrameUrl: string | undefined;
            if (lastFrameFile) {
              lastFrameUrl = await uploadFile(lastFrameFile);
            }

            doNavigate(uploadedUrl, lastFrameUrl);
          } catch (err: any) {
            console.error('[LandingBridge] upload failed:', err);
            onError?.(err.message || 'Upload failed');
          }
        })();
      });
    } else {
      // 无文件：直接写 Store + 跳转
      doNavigate();
    }
  });
}
